/**
 * Route guard for a single permission.
 *
 * Composes with `RequireAuth` instead of repeating it: this component NEVER
 * redirects. When there is no user it renders nothing, because the only place it
 * is used is inside the `RequireAuth`-wrapped layout route, which has already
 * sent an anonymous visitor to /login. Duplicating that redirect here would race
 * with it and could bounce a user whose session was just dropped twice.
 *
 * A missing permission is a dead end, not a redirect either: bouncing to the
 * dashboard would hide the reason. The German 403 panel names the required
 * permission (with the description from types.ts) and the roles the user
 * actually holds, which is exactly what an admin needs to fix the assignment.
 *
 * The permission strings are the frozen vocabulary of app/auth/permissions.py;
 * `Permission` makes a typo a compile error rather than a silent 403.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '../lib/auth';
import { PERMISSION_DESCRIPTIONS, ROLE_LABELS, type Permission } from '../lib/types';
import { Chip, ChipList } from './Chip';

export interface RequirePermissionProps {
  /** The permission the wrapped route needs — server vocabulary, verbatim. */
  permission: Permission;
  children: ReactNode;
}

export function RequirePermission({ permission, children }: RequirePermissionProps): ReactNode {
  const { user, has } = useAuth();

  // Not our job — RequireAuth owns the unauthenticated case.
  if (!user) return null;

  if (has(permission)) return <>{children}</>;

  return <PermissionDenied permission={permission} />;
}

/**
 * The 403 panel on its own, for pages that re-check their permission internally
 * (defence in depth against a stale bundle whose route table lost the guard).
 * Exported so there is exactly ONE wording of "you may not be here" in the SPA.
 */
export function PermissionDenied({ permission }: { permission: Permission }): ReactNode {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <div className="notice-panel">
      <div className="code">403</div>
      <h1>Keine Berechtigung</h1>
      <p>
        Für diese Seite wird die Berechtigung <code className="mono">{permission}</code> benötigt:{' '}
        {PERMISSION_DESCRIPTIONS[permission]}. Ihr Konto {user.email} besitzt sie nicht.
      </p>
      <div className="stack" style={{ alignItems: 'center', marginBottom: 18 }}>
        <span className="field-label">Zugewiesene Rollen</span>
        <ChipList>
          {user.roles.length === 0 ? (
            <Chip tone="warn">Keine Rolle zugewiesen</Chip>
          ) : (
            user.roles.map((role) => (
              <Chip key={role} tone={role === 'admin' ? 'info' : 'neutral'}>
                {ROLE_LABELS[role]}
              </Chip>
            ))
          )}
        </ChipList>
        <span className="hint muted">
          Eine Rollenänderung nimmt ein Administrator unter „Benutzer“ vor; sie greift nach dem
          nächsten Laden der Seite.
        </span>
      </div>
      {/*
        No "back to the dashboard" link when the dashboard is exactly what was
        refused — it would lead straight back to this panel.
      */}
      {permission === 'dashboard.read' ? (
        <span className="hint muted">
          Bis eine Rolle zugewiesen ist, bleibt nur die Abmeldung oben rechts.
        </span>
      ) : (
        <Link to="/">Zurück zum Dashboard</Link>
      )}
    </div>
  );
}

export default RequirePermission;
